// src/whatsappChat/components/Composer.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperclip,
  faPaperPlane,
  faLayerGroup,
  faListUl,
  faClock,
  faXmark,
  faFile,
  faWandMagicSparkles,
  faRotate,
  faSpinner,
  faBookmark,
} from '@fortawesome/free-solid-svg-icons';
import { uploadWaMedia, useWaChatSuggestions, useWaSendMessage } from '../api/hooks';
import { normalizeError, sendErrorMessage } from '../api/client';
import { patchThread } from '../api/cache';
import { formatBytes, MAX_TEXT, MAX_UPLOAD_BYTES, mediaTypeForFile } from '../api/content';
import { showToast } from '../../components/toast.jsx';
import { Modal } from './Modal';
import { TemplateForm } from './TemplateForm';
import { InteractiveBuilder } from './InteractiveBuilder';
import { ChatTemplateBar } from './ChatTemplateBar';

const COUNTER_FROM = MAX_TEXT - 300;

export function Composer({ conversationId, numberId, numberConfigured, windowOpen, onSent }) {
  const qc = useQueryClient();
  const sendMessage = useWaSendMessage();
  const [text, setText] = useState('');
  const [attachment, setAttachment] = useState(null); // { file, type, mediaId, mediaLink }
  const [uploading, setUploading] = useState(false);
  const [modal, setModal] = useState(null); // 'template' | 'interactive'
  const fileRef = useRef(null);
  const textRef = useRef(null);

  const chatSuggestions = useWaChatSuggestions();
  const [suggestions, setSuggestions] = useState([]);
  const [showAi, setShowAi] = useState(false);
  const [showChatTemplates, setShowChatTemplates] = useState(false);

  useEffect(() => {
    setSuggestions([]);
    setShowAi(false);
    setShowChatTemplates(false);
  }, [conversationId]);

  // The template picker and the suggested replies share one slot above the message box.
  const toggleChatTemplates = () => {
    setShowAi(false);
    setShowChatTemplates((open) => !open);
  };

  const fetchSuggestions = async () => {
    try {
      setShowChatTemplates(false);
      setShowAi(true);
      const res = await chatSuggestions.mutateAsync(conversationId);
      setSuggestions(res || []);
    } catch (err) {
      setShowAi(false);
      showToast.error(normalizeError(err).message || 'Failed to generate suggestions');
    }
  };

  const applySuggestion = (sug) => {
    setText(sug);
    if (textRef.current) {
      textRef.current.style.height = 'auto';
      textRef.current.style.height = `${Math.min(textRef.current.scrollHeight, 140)}px`;
      textRef.current.focus();
    }
  };

  const applyChatTemplate = (tpl) => {
    setText(tpl.template_body);
    // Sized after the new text has rendered, since a template can run to several lines.
    setTimeout(() => {
      if (textRef.current) {
        textRef.current.style.height = 'auto';
        textRef.current.style.height = `${Math.min(textRef.current.scrollHeight, 140)}px`;
        textRef.current.focus();
      }
    }, 30);
  };

  const busy = uploading || sendMessage.isPending;

  // One attempt only. A failed send is reported, never repeated on its own.
  const send = async (fields) => {
    try {
      await sendMessage.mutateAsync({ conversation_id: conversationId, ...fields });
      setShowAi(false);
      setShowChatTemplates(false);
      setSuggestions([]);
      onSent();
      return true;
    } catch (error) {
      if (normalizeError(error).code === 'window_closed') {
        patchThread(qc, conversationId, () => ({ windowOpen: false }));
      }
      showToast.error(sendErrorMessage(error));
      return false;
    }
  };

  const resetText = () => {
    setText('');
    if (textRef.current) textRef.current.style.height = 'auto';
    setShowAi(false);
    setSuggestions([]);
  };

  const submit = async () => {
    if (busy) return;
    const body = text.trim();

    if (attachment) {
      let mediaId = attachment.mediaId;
      let mediaLink = attachment.mediaLink;
      if (!mediaId) {
        setUploading(true);
        try {
          const uploaded = await uploadWaMedia(attachment.file, numberId);
          mediaId = uploaded.id;
          mediaLink = uploaded.url;
          // Kept so a failed send does not upload the file again.
          setAttachment((prev) => (prev ? { ...prev, mediaId, mediaLink } : prev));
        } catch (error) {
          showToast.error(sendErrorMessage(error));
          return;
        } finally {
          setUploading(false);
        }
      }
      const ok = await send({
        type: attachment.type,
        media_id: mediaId,
        ...(mediaLink ? { media_link: mediaLink } : {}),
        ...(body && attachment.type !== 'audio' ? { text: body } : {}),
        ...(attachment.type === 'document' ? { filename: attachment.file.name } : {}),
      });
      if (ok) {
        setAttachment(null);
        resetText();
      }
      return;
    }

    if (!body) return;
    if (body.length > MAX_TEXT) {
      showToast.error(`A message can be at most ${MAX_TEXT} characters.`);
      return;
    }
    if (await send({ type: 'text', text: body })) resetText();
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      showToast.error(`The file is ${formatBytes(file.size)}. The limit is 25 MB.`);
      return;
    }
    setAttachment({ file, type: mediaTypeForFile(file), mediaId: null, mediaLink: null });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const onTextChange = (e) => {
    setText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
  };

  const sendFromModal = async (fields) => {
    if (await send(fields)) setModal(null);
  };

  const modals = (
    <>
      {modal === 'template' && (
        <Modal title="Send a template" wide locked={busy} onClose={() => setModal(null)}>
          <TemplateForm
            numberId={numberId}
            sending={busy}
            onSend={sendFromModal}
            onCancel={() => setModal(null)}
          />
        </Modal>
      )}
      {modal === 'interactive' && (
        <Modal title="Send buttons or a list" locked={busy} onClose={() => setModal(null)}>
          <InteractiveBuilder sending={busy} onSend={sendFromModal} onCancel={() => setModal(null)} />
        </Modal>
      )}
    </>
  );

  if (!numberConfigured) {
    return (
      <footer className="wa-composer-banner is-danger">
        This business number is no longer configured, so replies cannot be sent.
      </footer>
    );
  }

  if (!windowOpen) {
    return (
      <footer className="wa-composer-banner">
        <span>
          <FontAwesomeIcon icon={faClock} /> The 24-hour window is closed. Only a template can be sent.
        </span>
        <button type="button" className="wa-btn wa-btn-primary" onClick={() => setModal('template')}>
          <FontAwesomeIcon icon={faLayerGroup} /> Send template
        </button>
        {modals}
      </footer>
    );
  }

  const caption = attachment && attachment.type !== 'audio';
  const canSubmit = !busy && (attachment ? true : Boolean(text.trim())) && text.length <= MAX_TEXT;

  return (
    <footer className="wa-composer">
      {attachment && (
        <div className="wa-attachment">
          <FontAwesomeIcon icon={faFile} />
          <span className="wa-attachment-name">{attachment.file.name}</span>
          <span className="wa-muted">
            {attachment.type} · {formatBytes(attachment.file.size)}
          </span>
          <button
            type="button"
            className="wa-icon-btn"
            onClick={() => setAttachment(null)}
            disabled={busy}
            aria-label="Remove attachment"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {showChatTemplates && (
        <ChatTemplateBar
          activeText={text}
          onPick={applyChatTemplate}
          onClose={() => setShowChatTemplates(false)}
        />
      )}

      {showAi && (
        <div className={`wa-ai-bar ${chatSuggestions.isPending ? 'is-thinking' : ''}`}>
          <div className="wa-ai-bar-header">
            <span className="wa-ai-title">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="wa-ai-title-icon" />
              Suggested Replies
            </span>
            <div className="wa-ai-bar-actions">
              <button
                type="button"
                className="wa-ai-icon-btn"
                onClick={fetchSuggestions}
                disabled={chatSuggestions.isPending}
                title="Regenerate suggestions"
                aria-label="Regenerate suggestions"
              >
                <FontAwesomeIcon icon={faRotate} spin={chatSuggestions.isPending} />
              </button>
              <button
                type="button"
                className="wa-ai-icon-btn"
                onClick={() => setShowAi(false)}
                title="Close"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>
          {chatSuggestions.isPending ? (
            <div className="wa-ai-thinking">
              <div className="wa-ai-thinking-header">
                <span className="wa-ai-sparkle-glow">
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                </span>
                <span className="wa-ai-thinking-text">Thinking & crafting suggestions</span>
                <span className="wa-ai-thinking-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </div>
              <div className="wa-ai-skeletons">
                <div className="wa-ai-skeleton-card">
                  <div className="wa-ai-skeleton-num"></div>
                  <div className="wa-ai-skeleton-lines">
                    <div className="wa-ai-skeleton-line w-85"></div>
                    <div className="wa-ai-skeleton-line w-45"></div>
                  </div>
                </div>
                <div className="wa-ai-skeleton-card">
                  <div className="wa-ai-skeleton-num"></div>
                  <div className="wa-ai-skeleton-lines">
                    <div className="wa-ai-skeleton-line w-70"></div>
                    <div className="wa-ai-skeleton-line w-60"></div>
                  </div>
                </div>
                <div className="wa-ai-skeleton-card">
                  <div className="wa-ai-skeleton-num"></div>
                  <div className="wa-ai-skeleton-lines">
                    <div className="wa-ai-skeleton-line w-85"></div>
                    <div className="wa-ai-skeleton-line w-35"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : suggestions.length > 0 ? (
            <div className="wa-ai-chips">
              {suggestions.map((sug, idx) => {
                const isSelected = text === sug;
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`wa-ai-chip${isSelected ? ' is-selected' : ''}`}
                    onClick={() => applySuggestion(sug)}
                    title="Click to insert this reply"
                  >
                    <div className="wa-ai-chip-main">
                      <span className="wa-ai-chip-num">{idx + 1}</span>
                      <span className="wa-ai-chip-text">{sug}</span>
                    </div>
                    <span className="wa-ai-chip-action">{isSelected ? 'Selected ✓' : 'Use ↵'}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#94a3b8', padding: '6px 2px' }}>
              No suggestions available. Click refresh to try again.
            </div>
          )}
        </div>
      )}

      <div className="wa-composer-row">
        <input ref={fileRef} type="file" hidden onChange={onPickFile} />
        <button
          type="button"
          className="wa-icon-btn"
          title="Attach a file (up to 25 MB)"
          aria-label="Attach a file"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          <FontAwesomeIcon icon={faPaperclip} />
        </button>
        <button
          type="button"
          className="wa-icon-btn"
          title="Send a template"
          aria-label="Send a template"
          onClick={() => setModal('template')}
          disabled={busy}
        >
          <FontAwesomeIcon icon={faLayerGroup} />
        </button>
        <button
          type="button"
          className="wa-icon-btn"
          title="Send buttons or a list"
          aria-label="Send buttons or a list"
          onClick={() => setModal('interactive')}
          disabled={busy}
        >
          <FontAwesomeIcon icon={faListUl} />
        </button>
        <button
          type="button"
          className={`wa-icon-btn${showChatTemplates ? ' is-active' : ''}`}
          title="Insert a chat template"
          aria-label="Insert a chat template"
          aria-expanded={showChatTemplates}
          onClick={toggleChatTemplates}
          disabled={busy || (attachment && !caption)}
        >
          <FontAwesomeIcon icon={faBookmark} />
        </button>
        <button
          type="button"
          className="wa-icon-btn wa-composer-btn-ai"
          title="Suggested Replies"
          aria-label="Suggested Replies"
          onClick={fetchSuggestions}
          disabled={busy || chatSuggestions.isPending}
        >
          <FontAwesomeIcon icon={chatSuggestions.isPending ? faSpinner : faWandMagicSparkles} spin={chatSuggestions.isPending} />
        </button>

        <textarea
          ref={textRef}
          className="wa-composer-input"
          rows={1}
          value={text}
          onChange={onTextChange}
          onKeyDown={onKeyDown}
          disabled={busy || (attachment && !caption)}
          placeholder={
            attachment ? (caption ? 'Add a caption (optional)' : 'Audio is sent without a caption') : 'Type a message'
          }
          aria-label="Message"
        />

        <button
          type="button"
          className="wa-btn wa-btn-primary wa-composer-send"
          onClick={submit}
          disabled={!canSubmit}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
          <span className="wa-hide-sm">{uploading ? 'Uploading…' : sendMessage.isPending ? 'Sending…' : 'Send'}</span>
        </button>
      </div>

      {text.length > COUNTER_FROM && (
        <div className={`wa-composer-count${text.length > MAX_TEXT ? ' is-over' : ''}`}>
          {text.length}/{MAX_TEXT}
        </div>
      )}
      {modals}
    </footer>
  );
}
